import React, {useState} from 'react';
import ProviderSecondaryLocations from './ProviderSecondaryLocations.jsx';
import SlotGrid from './SlotGrid.jsx';
import ReviewsAndRatings from '@js/reviews-and-ratings.es6.js';
import ScheduleProviderCardUtils from '@src/js/provider-card-utils.js';
import { ResultType } from '@src/js/constants.js';
import { ContentUtils, SessionStorageUtils } from '@js/utils/utils-imports.es6.js';
import {
  PrimaryLinkButton,
  SecondaryLinkButton,
} from '@src/components/core/core-component-imports.js';
import {
  ColorAdjustedEmeraldHelpIcon,
  ColorAdjustedEmeraldLuminisHealthCircleIcon,
  MidnightVideoCameraIcon,
} from '@src/images/image-imports.js';

const ScheduleProviderCard = ({
                                affiliateProviderLabel,
                                alias,
                                hideLocationText,
                                image,
                                initialLocation,
                                distanceAwayText,
                                isAcceptingTelehealth,
                                isAcceptingTelehealthLabel,
                                isAffiliate,
                                luminisProviderLabel,
                                name,
                                noRatingsLink,
                                noRatingsText,
                                npi,
                                otherLocations,
                                providerEpicId = null,
                                ratingsLabel,
                                remainingLocations,
                                specialties,
                                viewMoreEndingText,
                                viewMoreEndingTextSingular,
                                viewMoreStartingText,
                                viewMoreTimesText = null,
                                viewFewerTimesText = null,
                                userLocation = null,
                                geolocationService = null,
                                milesLabel,
                                awayLabel,
                                hasOnlineAvailability,
                                scheduleAnAppointmentLabel,
                                scheduleAnAppointmentPath,
                                callLabel,
                                showSchedulingOptions,
                                isAcceptingNewPatients,
                                acceptingNewPatientsLabel,
                                nextAvailableText,
                                triggerRedraw,
                                triggerRedrawByIncrementing
                              }) => {
  const [showExtraLocations, updateLocationState] = useState(false);
  const [providerRatings, getProviderRatings] = useState(false);

  const getCallText = location => {
    return `${callLabel} ${ScheduleProviderCardUtils.getLocationPhoneNumber(
      location.field_phone_1,
    )}`;
  };

  const getCallLink = location => {
    return `${ScheduleProviderCardUtils.getLocationPhoneNumberLink(
      location.field_phone_1,
    )}`;
  }

  const setDistanceAwayText = location => {
    if (userLocation) {
      return `${geolocationService
        .convertKilometersToMiles(location.field_geolocation_distance)
        .toFixed(2)} ${milesLabel} ${awayLabel}`;
    }
    return '';
  };

  const getLocationDepartmentId = location => {
    if (location.department) {
      return location.department.id;
    }
    return null;
  };

  const getLocationName = location => {
    if (location.department) {
      return ContentUtils.decodeHtml(location.department.name);
    }
    return ContentUtils.decodeHtml(location.field_location_name_1);
  };

  const getLocationAddress = location => {
    if (location.department) {
      return ContentUtils.decodeHtml(`${location.department.address.line1}, ${location.department.address.city}, ${location.department.address.state} ${location.department.address.zipCode}`);
    }
    return ContentUtils.decodeHtml(location.field_location_address);
  };

  const setOtherLocationState = () => {
    updateLocationState(!showExtraLocations);
  };

  const getProviderOverallRatings = npi => {
    const reviewsAndRatings = new ReviewsAndRatings(npi);
    reviewsAndRatings.starsOnlyWidget(jQuery);
    getProviderRatings(!providerRatings);
  };

  const scheduleOnlineClickHandler = () => SessionStorageUtils.clearAppointmentSpecificSessionStorage();

  return (
    <div key={npi} className='providers__card'>
      <span id={'ratings-label-' + npi} className='ratings-label hidden'>{ratingsLabel}</span>
      <div className='card__initial-details'>
        <div className='initial-details__provider-info'>
          <img className='provider-info__image' src={image} />
          <a href={alias} className='provider-info__name'>{ContentUtils.decodeHtml(name)}</a>
          <h4 className='provider-info__specialties'>{specialties}</h4>
          <span id={'star-ratings-' + npi} className='provider-info__ratings provider-star-ratings'>
            <div id={'overall-' + npi} className='overall-star-rating'></div>
            <div id={'no-star-' + npi} className='no-star-ratings'>
              <a className='hidden' href={noRatingsLink.replace('entity:', `${window.location.origin}/`)} target='_blank'>
                {noRatingsText}
              </a>
            </div>
            { !providerRatings &&
              getProviderOverallRatings(npi)
            }
          </span>
          <span className='provider-info__affiliation'>
          {
            isAffiliate
            ? (
              <>
                {affiliateProviderLabel}
                <span className='affiliation__icon' tabIndex='0'>
                  <ColorAdjustedEmeraldHelpIcon />
                </span>
              </>
            )
            : (
              <>
                <span className='affiliation__luminis-icon'>
                  <ColorAdjustedEmeraldLuminisHealthCircleIcon />
                </span>
                {luminisProviderLabel}
              </>
            )
          }
          </span>
          {
            (isAcceptingTelehealth || isAcceptingNewPatients) &&
            <div className='provider-badges'>
              {
                isAcceptingTelehealth &&
                <span className='provider-info__telehealth'>
                  <span className='telehealth__icon'>
                    <MidnightVideoCameraIcon />
                  </span>
                  {isAcceptingTelehealthLabel}
                </span>
              }
              {
                isAcceptingNewPatients &&
                <span className='provider-info__new-patients'>
                  {acceptingNewPatientsLabel}
                </span>
              }
            </div>
          }
        </div>
        { initialLocation &&
          <>
            <div className='initial-details__location'>
              <span className='location__distance'>
                { distanceAwayText ? distanceAwayText : setDistanceAwayText(initialLocation) }
              </span>
              <h4 className='location__name'>{getLocationName(initialLocation)}</h4>
              <p className='location__address'>{getLocationAddress(initialLocation)}</p>
              { !showExtraLocations && remainingLocations.length > 1 &&
                <button onClick={setOtherLocationState} className='location__show'>{viewMoreStartingText} {remainingLocations.length} {viewMoreEndingText}</button>
              }
              { !showExtraLocations && remainingLocations.length == 1 &&
                <button onClick={setOtherLocationState} className='location__show'>{viewMoreStartingText} {remainingLocations.length} {viewMoreEndingTextSingular}</button>
              }
              { showExtraLocations && remainingLocations.length > 0 &&
                <button onClick={setOtherLocationState} className='location__hide'>{hideLocationText}</button>
              }
            </div>
            { initialLocation.department &&
              <div
                className='initial-details__scheduling-info scheduling-info'
                data-deptid={initialLocation.department.id}
                data-providerid={providerEpicId}
                data-npi={npi}
                data-resulttype={ResultType.PROVIDERS}
              >
                <SlotGrid
                  key={`${npi}-${initialLocation.department.id}`}
                  availabilityByDays={initialLocation.availabilityByDays}
                  viewMoreTimesText={viewMoreTimesText}
                  viewFewerTimesText={viewFewerTimesText}
                  triggerRedraw={triggerRedraw}
                  triggerRedrawByIncrementing={triggerRedrawByIncrementing}
                  nextAvailableText={nextAvailableText}
                />
              </div>
            }
          </>
        }
        {
          showSchedulingOptions &&
          <div className='scheduling-cta-buttons'>
            {
              hasOnlineAvailability &&
              <PrimaryLinkButton
                href={`${scheduleAnAppointmentPath}?npi=${npi}`}
                clickHandler={scheduleOnlineClickHandler}
                className='cta-button provider-card-schedule-button'
              >
                {scheduleAnAppointmentLabel}
              </PrimaryLinkButton>
            }
            {initialLocation && initialLocation.field_phone_1 &&
              <SecondaryLinkButton
                href={getCallLink(initialLocation)}
                className='cta-button'
              >
                {getCallText(initialLocation)}
              </SecondaryLinkButton>
            }
          </div>
        }
        </div>
        <div className='card__other-locations'>
          {otherLocations &&
          <>
            {
              otherLocations.map((location) => {
                return <ProviderSecondaryLocations
                  key={npi + '-' + getLocationName(location)}
                  {...location}
                  name={getLocationName(location)}
                  address={getLocationAddress(location)}
                  id={getLocationDepartmentId(location)}
                  providerEpicId={providerEpicId}
                  npi={npi}
                  viewMoreTimesText={viewMoreTimesText}
                  viewFewerTimesText={viewFewerTimesText}
                  distanceAwayText={setDistanceAwayText(location)}
                  triggerRedraw={triggerRedraw}
                  triggerRedrawByIncrementing={triggerRedrawByIncrementing}
                  nextAvailableText={nextAvailableText}
                />
              })
            }
          </>
          }
          { showExtraLocations &&
            <>
              {
                remainingLocations.map((location) => {
                  return <ProviderSecondaryLocations
                    key={npi + '-' + getLocationName(location)}
                    {...location}
                    name={getLocationName(location)}
                    address={getLocationAddress(location)}
                    id={getLocationDepartmentId(location)}
                    providerEpicId={providerEpicId}
                    npi={npi}
                    viewMoreTimesText={viewMoreTimesText}
                    viewFewerTimesText={viewFewerTimesText}
                    distanceAwayText={setDistanceAwayText(location)}
                    triggerRedraw={triggerRedraw}
                    triggerRedrawByIncrementing={triggerRedrawByIncrementing}
                    nextAvailableText={nextAvailableText}
                  />
                })
              }
            </>
          }
        </div>
      </div>
  );
}

export default ScheduleProviderCard;
